import React from 'react';
import { SegmentGroup as ArkSegmentGroup } from '@ark-ui/react/segment-group';
import clsx from 'clsx';
import styles from './SegmentGroup.module.scss';

export interface SegmentOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface SegmentGroupProps {
  options: SegmentOption[];
  value: string;
  onValueChange: (value: string) => void;
  orientation?: 'horizontal' | 'vertical';
  disabled?: boolean;
  readOnly?: boolean;
  /** Chybový stav — vizuálně zvýrazní skupinu. */
  invalid?: boolean;
  onBlur?: () => void;
  className?: string;
  'data-testid'?: string;
  /** Přístupné jméno skupiny, když ji nepojmenovává `aria-labelledby`. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  /** Další atributy na kořenový prvek — QA `data-*` z `SegmentGroupInput`. */
  rootProps?: Record<string, string>;
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * Segmentový přepínač (volba jedné z několika možností) postavený na Ark UI
 * `SegmentGroup` — jediná implementace tohoto vzhledu v knihovně.
 *
 * Sémanticky jde o `radiogroup`, ne o taby: hodí se tam, kde volba přepíná
 * obsah stránky, který **není** v panelu pod přepínačem. Pro skutečné taby
 * s panely je `Tabs`.
 *
 * Řízený prostým `value`/`onValueChange`, takže se dá použít i mimo formulář.
 * Ve formuláři se používá `SegmentGroupInput`, který je tenkou nadstavbou
 * nad touto komponentou.
 */
export const SegmentGroup: React.FC<SegmentGroupProps> = ({
  options,
  value,
  onValueChange,
  orientation = 'horizontal',
  disabled,
  readOnly,
  invalid,
  onBlur,
  className,
  rootProps,
  ref,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}) => (
  <ArkSegmentGroup.Root
    ref={ref}
    value={value || null}
    disabled={disabled}
    readOnly={readOnly}
    invalid={invalid}
    orientation={orientation}
    onValueChange={(details) => onValueChange(details.value ?? '')}
    onBlur={onBlur}
    className={clsx(styles.segmentRoot, className)}
    data-testid={dataTestId}
    aria-label={ariaLabel}
    aria-labelledby={ariaLabelledBy}
    aria-describedby={ariaDescribedBy}
    {...rootProps}
  >
    <ArkSegmentGroup.Indicator className={styles.segmentIndicator} />
    {options.map((option) => (
      <ArkSegmentGroup.Item
        key={option.value}
        value={option.value}
        disabled={option.disabled}
        className={styles.segmentItem}
        data-testid={dataTestId ? `${dataTestId}-option-${option.value}` : undefined}
      >
        <ArkSegmentGroup.ItemText className={styles.segmentItemText}>
          {option.label}
        </ArkSegmentGroup.ItemText>
        <ArkSegmentGroup.ItemControl />
        <ArkSegmentGroup.ItemHiddenInput />
      </ArkSegmentGroup.Item>
    ))}
  </ArkSegmentGroup.Root>
);

SegmentGroup.displayName = 'SegmentGroup';
